import React from 'react';
import { flushSync } from 'react-dom';
import { Chip, Stack, Typography } from '@mui/material';
import {
  FolderShared,
  UnfoldLessOutlined,
  UnfoldMoreOutlined,
} from '@mui/icons-material';

import { GroupToggle } from 'components/common/GroupToggle/GroupToggle';
import { belowAppBar } from '@theme/variables';

const pinnedTop = belowAppBar('0px');

interface ICardGroupProps {
  name: string;
  title?: string;
  count: number;
  collapsed: boolean;
  disabled: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export const CardGroup: React.FC<ICardGroupProps> = ({
  name,
  title,
  count,
  collapsed,
  disabled,
  onToggle,
  children,
}) => {
  const FoldIcon = collapsed ? UnfoldMoreOutlined : UnfoldLessOutlined;

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    const toggle = event.currentTarget;
    flushSync(onToggle);
    toggle.scrollIntoView({ block: 'nearest' });
  };

  return (
    <Stack gap={1} pb={collapsed ? 0 : 3}>
      <GroupToggle
        collapsed={collapsed}
        disabled={disabled}
        onClick={handleToggle}
        sx={{
          position: 'sticky',
          top: pinnedTop,
          scrollMarginTop: pinnedTop,
          zIndex: 1,
          '&.Mui-disabled': { pointerEvents: 'auto' },
          gap: 1,
          p: 1.25,
          borderRadius: 1,
          backgroundColor: collapsed
            ? 'rgba(0, 0, 0, 0.06)'
            : 'background.backgroundPrimary',
        }}
      >
        <Chip
          component="span"
          size="small"
          icon={<FolderShared />}
          label={name}
          sx={{ minWidth: 0, ...(title && { flexShrink: 0, maxWidth: '50%' }) }}
        />
        {title && (
          <Typography component="span" variant="body2" noWrap minWidth={0}>
            {title}
          </Typography>
        )}
        <Typography component="span" variant="body2" color="text.disabled">
          {count}
        </Typography>
        <FoldIcon
          fontSize="small"
          sx={{ ml: 'auto', color: 'text.secondary' }}
        />
      </GroupToggle>
      {!collapsed && children}
    </Stack>
  );
};
